import { Box, Grid, Typography, Button, Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ConfirmationButton, createNumberFormatField, createTextField, Paper, SelectField } from '@roc/ui';
import { observer } from 'mobx-react';
import { useLoanVendorStore } from '../../hooks/userLoanVendorStore';
import { LoanVendorSearchBar } from '../loan/loanVendorSearchBar';
import { useEffect } from 'react';
import { CounterPartyExpensesTab } from '../../types/types';
import { bankInformationPropertyStates, isNotBlank } from '@roc/feature-utils';

const enum Status {
  APPROVED = 'Approved',
  REQUESTED = 'Requested',
  PENDING_APPROVAL = 'Pending Approval',
  NOT_VERIFIED = 'Not Verified',
}

const useStyles = makeStyles(theme => ({
  buttonTop: {
    marginLeft: '10px',
  },
  gridChip: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: '10px',
  },
  approvedChip: {
    backgroundColor: '#49be25',
    letterSpacing: 2,
    color: '#000000',
    fontWeight: 'bold',
    marginLeft: '5px'
  },
  notRequestedChip: {
    backgroundColor: '#be2528',
    letterSpacing: 2,
    color: '#ffffff',
    fontWeight: 'bold',
    marginLeft: '5px'
  },
  pendingApprovalChip: {
    backgroundColor: '#ffcc00',
    letterSpacing: 2,
    color: '#000000',
    fontWeight: 'bold',
    marginLeft: '5px'
  },
  requestedChip: {
    backgroundColor: '#007bff',
    letterSpacing: 2,
    color: '#ffffff',
    fontWeight: 'bold',
    marginLeft: '5px'
  },
}));


export const BankInformationApproval = observer(() => {
  const classes = useStyles();
  const { loanVendorStore } = useLoanVendorStore();
  const { vendorBankDetailsFormStore, selectedTab } = loanVendorStore;

  const status = loanVendorStore.vendor?.bankInfoVerified;

  useEffect(() => {
    vendorBankDetailsFormStore.reset();
    if (loanVendorStore.loanVendor && selectedTab === CounterPartyExpensesTab.VENDOR_APPROVAL) {
      loanVendorStore.loadBankDetailsForm();
    }
  }, [loanVendorStore.loanVendor]);

  return (
    <>
      <Grid container style={{ padding: '16px' }} >
        <Grid item sm={4} xs={12} >
          <LoanVendorSearchBar />
        </Grid>
        <Grid item xs={12} style={{ marginBottom: 24, marginTop: 16 }} >
          <Box width={"100%"} height={'1px'} bgcolor={"#ddd"} mt={1}></Box>
        </Grid>
        <Grid item container style={{ marginBottom: 16 }} justifyContent="space-between">
          <Grid item sm={6} xs={12} className={classes.gridChip}>
            <Typography variant="h5">BANK INFORMATION</Typography>
            <Chip
              label={`${status ? 'VERIFIED' : 'NOT VERIFIED'}`}
              className={status ? classes.approvedChip : classes.notRequestedChip}
            />
          </Grid>
          <Grid item sm={6} xs={12} justifyContent={"flex-end"} style={{ display: 'flex' }} spacing={1}>
            {/*<Button
              color="primary"
              variant="contained"
              onClick={() => { }}
              disabled={!loanVendorStore.vendor}
            >
              Download Signed Details
            </Button>*/}
          </Grid>
        </Grid>
        <Grid item container spacing={2}>
          <Grid item sm={6} xs={12}>
            {createTextField({
              store: vendorBankDetailsFormStore,
              testId: 'accountName',
              fieldName: 'beneficiaryAccountName',
              label: 'Account Name',
              disabled: !loanVendorStore.vendor
            })}
          </Grid>
          <Grid item sm={6} xs={12}>
            {createTextField({
              store: vendorBankDetailsFormStore,
              testId: 'bankName',
              fieldName: 'beneficiaryBankName',
              label: 'Bank Name',
              disabled: !loanVendorStore.vendor
            })}
          </Grid>
          <Grid item sm={6} xs={12}>
            {createNumberFormatField({
              store: vendorBankDetailsFormStore,
              testId: 'routingNumber',
              fieldName: 'beneficiaryRoutingNumber',
              label: 'Routing Number',
              format: '#########',
              disabled: !loanVendorStore.vendor
            })}
          </Grid>
          <Grid item sm={6} xs={12}>
            {createNumberFormatField({
              store: vendorBankDetailsFormStore,
              testId: 'accountNumber',
              fieldName: 'beneficiaryAccountNumber',
              label: 'Account Number',
              disabled: !loanVendorStore.vendor
            })}
          </Grid>
          <Grid item xs={12} sm={6} >
            {createTextField({
              label: 'Street Name',
              fieldName: 'streetName',
              testId: 'streetName',
              store: vendorBankDetailsFormStore,
              disabled: true,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createTextField({
              label: 'City',
              fieldName: 'city',
              testId: 'city',
              store: vendorBankDetailsFormStore,
              disabled: true,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createTextField({
              label: 'Zip Code',
              fieldName: 'zipCode',
              testId: 'zipCode',
              store: vendorBankDetailsFormStore,
              disabled: true,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectField
              standaloneLabel
              label={'State'}
              name={'state'}
              value={vendorBankDetailsFormStore.form.fields.state.value}
              error={isNotBlank(
                vendorBankDetailsFormStore.form.fields.state.error
              )}
              errorText={
                vendorBankDetailsFormStore.form.fields.state.error
              }
              options={bankInformationPropertyStates.map(s => ({ label: s.label, value: s.id }))}
              onChange={label =>
                vendorBankDetailsFormStore.onFieldChange('state', label)
              }
              variant="outlined"
              fullWidth
              shrink
              testId="state"
              disabled
            />
          </Grid>
          <Grid item xs={12} justifyContent={"flex-end"} style={{ display: 'flex' }} spacing={1}>
            <ConfirmationButton
              onClick={() => loanVendorStore.verifyBankDetails()}
              className={classes.buttonTop}
              disabled={!loanVendorStore.vendor}
              color="primary"
              variant="contained"
              testId="confirm"
              confirmDialogProps={{
                title: 'Confirmation',
                body:
                  "Are you sure you want to mark this bank information as verified?",
              }}
            >
              Verify Bank Info
            </ConfirmationButton>
            <ConfirmationButton
              onClick={() => loanVendorStore.saveBankDetails()}
              className={classes.buttonTop}
              disabled={!loanVendorStore.vendor}
              color="primary"
              variant="contained"
              testId="confirm"
              confirmDialogProps={{
                title: 'Confirmation',
                body:
                  "Are you sure you want to update the bank information?",
              }}
            >
              Update Bank Info
            </ConfirmationButton>
          </Grid>
        </Grid>
      </Grid>

    </>
  );
});
